import { useState } from "react";
import { Clock, MapPin, Phone, Mail, Send } from "lucide-react";
import { restaurant } from "../mock";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../i18n";
import { sendOwnerEmail } from "../lib/sendOwnerEmail";

// 0 = Sunday, 1 = Monday, 2 = Tuesday ... 6 = Saturday
// Each period is [first bookable time, last bookable time].
// An empty list [] means no bookings that day.
const SCHEDULE = {
  0: [],
  1: [["12:00", "14:00"], ["18:00", "21:00"]],
  2: [["18:00", "21:00"]],
  3: [["18:00", "21:00"]],
  4: [["18:00", "21:00"]],
  5: [["12:00", "14:00"], ["18:00", "21:00"]],
  6: [],
};

const toMin = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
const pad = (n) => String(n).padStart(2, "0");

// Returns the bookable times (every 30 min) for a date like "2026-10-09"
const slotsFor = (dateStr) => {
  if (!dateStr) return [];
  const day = new Date(dateStr + "T12:00:00").getDay();
  const out = [];
  (SCHEDULE[day] || []).forEach(([from, last]) => {
    for (let m = toMin(from); m <= toMin(last); m += 30) {
      out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
    }
  });
  return out;
};

const inputCls =
  "w-full rounded-xl border border-[#E7DAC6] bg-[#FAF4EA] px-4 py-3 outline-none focus:border-[#9C3B2E] focus:ring-2 focus:ring-[#9C3B2E]/20 transition";

export default function Visit() {
  const { toast } = useToast();
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", date: "", time: "", guests: "2", email: "", phone: "", message: "", honey: "" });
  const [submitting, setSubmitting] = useState(false);
  const slots = slotsFor(form.date);

  const hours = [
    { label: t("hours.lunch"), days: t("hours.lunch.days"), time: "12:00 – 15:00" },
    { label: t("hours.dinner"), days: t("hours.dinner.days"), time: "18:00 – 23:30" },
    { label: t("hours.sat"), days: t("hours.sat.days"), time: t("hours.sat.time") },
    { label: t("hours.takeaway"), days: t("hours.takeaway.days"), time: t("hours.takeaway.time") },
  ];

  const isEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
  // en-CA formats as YYYY-MM-DD, which is what <input type="date"> expects.
  const today = new Date().toLocaleDateString("en-CA");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.date || !form.time || !isEmail(form.email) || !form.phone) {
      toast({ title: t("visit.toast.fillTitle"), description: t("visit.toast.fillDesc") });
      return;
    }
    if (form.date < today) {
      toast({ title: t("visit.toast.pastTitle"), description: t("visit.toast.pastDesc") });
      return;
    }
    if (!slots.includes(form.time)) {
      toast({
        title: "Geschlossen · Chiuso",
        description: "Bitte wähle einen Tag und eine Uhrzeit innerhalb unserer Öffnungszeiten.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await sendOwnerEmail({
        subject: `Reservierung – ${form.name} · ${form.guests} Gäste · ${form.date.slice(8, 10)}.${form.date.slice(5, 7)}. um ${form.time}`,
        replyTo: form.email,
        honey: form.honey,
        autoresponse: t("mail.autoBooking"),
        fields: {
          Typ: "Reservierung",
          Name: form.name,
          Datum: form.date,
          Uhrzeit: form.time,
          Gäste: form.guests,
          email: form.email,
          Telefon: form.phone,
          Nachricht: form.message || "—",
        },
      });
      toast({
        title: "Grazie mille, " + form.name + "!",
        description: `${form.date} · ${form.time} · ${form.guests} ${form.guests === "1" ? t("visit.form.person") : t("visit.form.persons")}`,
      });
      setForm({ name: "", date: "", time: "", guests: "2", email: "", phone: "", message: "", honey: "" });
    } catch (err) {
      toast({ title: t("visit.toast.errTitle"), description: t("visit.toast.errDesc") });
    }
    setSubmitting(false);
  };

  return (
    <section id="visit" className="py-24 lg:py-32 bg-[#FAF4EA]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="reveal text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 text-[#9C3B2E] text-xs uppercase tracking-[0.35em] mb-5">
            <span className="w-8 h-px bg-[#9C3B2E]" /> {t("visit.eyebrow")} <span className="w-8 h-px bg-[#9C3B2E]" />
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-700 text-[#2A2420] leading-tight">
            {t("visit.title1")} <span className="italic text-[#9C3B2E]">{t("visit.title2")}</span> {t("visit.title3")}
          </h2>
          <p className="mt-5 text-[#6b6058] font-body">{t("visit.sub")}</p>
        </div>

        <div className="grid lg:grid-cols-2 lg:grid-rows-[auto_1fr] gap-x-10 gap-y-8 lg:gap-y-6">
          <div className="contents">
            <div className="reveal order-1 lg:order-none lg:col-start-1 lg:row-start-1 lg:self-start rounded-3xl bg-white p-8 shadow-[0_20px_50px_-25px_rgba(42,36,32,0.35)] border border-[#EFE4D2]">
              <h3 className="font-display text-2xl font-600 text-[#2A2420] flex items-center gap-3">
                <Clock className="w-6 h-6 text-[#9C3B2E]" /> {t("visit.hours")}
              </h3>
              <ul className="mt-6 divide-y divide-[#EFE4D2]">
                {hours.map((h) => (
                  <li key={h.label} className="py-4 flex items-start justify-between gap-4">
                    <div>
                      <span className="block font-600 text-[#2A2420]">{h.label}</span>
                      <span className="block text-sm text-[#6b6058]">{h.days}</span>
                    </div>
                    <span className="font-500 text-[#9C3B2E] whitespace-nowrap">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="reveal order-3 lg:order-none lg:col-start-1 lg:row-start-2 lg:self-start grid sm:grid-cols-3 gap-4 items-stretch">
              {[
                { icon: MapPin, label: t("visit.addr"), value: restaurant.address },
                { icon: Phone, label: t("visit.phone"), value: restaurant.phone },
                { icon: Mail, label: t("visit.email"), value: restaurant.email },
              ].map((c) => (
                <div key={c.label} className="h-full flex flex-col rounded-2xl bg-[#9C3B2E] text-[#FAF4EA] p-5">
                  <c.icon className="w-5 h-5 text-[#F0D9A7]" />
                  <span className="block mt-3 text-[11px] uppercase tracking-wider text-[#F0D9A7]/80">{c.label}</span>
                  <span className="block mt-1 text-sm leading-snug break-words">{c.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2">
            <form onSubmit={submit} className="rounded-3xl bg-white p-8 shadow-[0_20px_50px_-25px_rgba(42,36,32,0.35)] border border-[#EFE4D2] h-full flex flex-col">
              <h3 className="font-display text-2xl font-600 text-[#2A2420]">{t("visit.form.title")}</h3>
              <p className="mt-3 rounded-xl bg-[#9C3B2E]/8 border border-[#9C3B2E]/20 px-4 py-3 text-sm leading-relaxed text-[#6b4a3f]">
                {t("visit.form.notice")}
              </p>

              <div className="mt-6 space-y-5 flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("visit.form.name")}</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("visit.form.guests")}</label>
                    <select
                      value={form.guests}
                      onChange={(e) => setForm({ ...form, guests: e.target.value })}
                      className={inputCls}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                        <option key={n} value={n}>{n} {n === 1 ? t("visit.form.person") : t("visit.form.persons")}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="min-w-0">
                    <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("visit.form.date")}</label>
                    <input
                      type="date"
                      min={today}
                      value={form.date}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v && slotsFor(v).length === 0) {
                          toast({
                            title: "Geschlossen · Chiuso",
                            description: "An diesem Tag sind wir nur für private Feiern und Events geöffnet. Wenn du dafür anfragen möchtest, besuche bitte die Seite „Events & Catering“. · In questo giorno siamo aperti solo per feste private ed eventi. Per richiederli, visita la pagina «Eventi & Catering».",
                          });
                          setForm({ ...form, date: "", time: "" });
                          return;
                        }
                        setForm({ ...form, date: v, time: "" });
                      }}
                      className={`${inputCls} block w-full min-w-0 max-w-full h-[50px] appearance-none [-webkit-appearance:none] text-left`}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-500 text-[#4A423B] mb-2">Uhrzeit / Orario</label>
                    <select
                      value={form.time}
                      onChange={(e) => setForm({ ...form, time: e.target.value })}
                      disabled={slots.length === 0}
                      className={inputCls + " disabled:opacity-60"}
                    >
                      <option value="">{slots.length === 0 ? "—" : "Bitte wählen"}</option>
                      {slots.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("visit.form.email")}</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      placeholder="mario@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("visit.form.phone")}</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputCls}
                      placeholder="+49 ..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("visit.form.msgOpt")}</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full rounded-xl border border-[#E7DAC6] bg-[#FAF4EA] px-4 py-3 outline-none focus:border-[#9C3B2E] focus:ring-2 focus:ring-[#9C3B2E]/20 transition resize-none"
                    placeholder={t("visit.form.msgPh")}
                  />
                </div>
              </div>

              <input
                type="text"
                name="_honey"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.honey}
                onChange={(e) => setForm({ ...form, honey: e.target.value })}
                className="absolute -left-[9999px] w-px h-px opacity-0"
              />

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#9C3B2E] text-[#FAF4EA] font-500 tracking-wide hover:bg-[#9C3B2E] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-10px_rgba(181,86,58,0.7)] disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? t("visit.form.sending") : t("visit.form.submit")} <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="reveal mt-10 rounded-3xl overflow-hidden border border-[#EFE4D2] shadow-[0_20px_50px_-25px_rgba(42,36,32,0.35)]">
          <iframe
            title="Standort Quattro Tavoli"
            src="https://www.google.com/maps?q=Dreim%C3%BChlenstra%C3%9Fe%2010%2C%2080469%20M%C3%BCnchen&output=embed"
            width="100%"
            height="380"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
}